import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {EventPricing, EventPricingTicketType, Limit, LimitDetail, Promocode} from '../../generated/sales-terminal-client';
import {SnackService} from '../../services/snack/snack.service';
import {TICKET_LIMIT} from '../../modules/sales-terminal/sales-terminal.constants';
import {findIndex, orderBy, uniq} from 'lodash-es';
import {StorageService} from "../../services/storage/storage.service";
import {AuthService} from "../../services/auth/auth.service";
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {UtilService} from "../../services/util/util.service";
import {CartAdvancedInterface} from '../../services/cart/cart.interface';
import {tap} from 'rxjs';
import {STORAGE_PROMOCODE_STORAGE} from "../../modules/sales-terminal/sales-terminal.constants";

@Component({
  selector: 'app-promocode',
  templateUrl: './promocode.component.html',
  styleUrls: ['./promocode.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PromocodeComponent<Type> implements OnInit {
  @ViewChild(MatAutocompleteTrigger) codesCacheAutocomplete: MatAutocompleteTrigger;
  public codesCache: string[] = [];
  public selfServicePromocode: string;

  @Input() service: CartAdvancedInterface<Type>;
  @Input() selfService: boolean;

  private _eventPricing: EventPricing;
  @Input()
  public set eventPricing(value: EventPricing) {
    this._eventPricing = value;
    this.selfServicePromocode = null;
    this.setActiveSelfServicePromocode();
  }
  public get eventPricing(): EventPricing {
    return this._eventPricing;
  }

  private _promocode: string;
  @Input()
  public set promocode(value: string) {
    this._promocode = value;
    this.promocodeChange.emit(value);
  }
  public get promocode(): string {
    return this._promocode;
  }
  @Output() public promocodeChange: EventEmitter<string> = new EventEmitter<string>();

  private _eventIds: number[] = []
  @Input()
  public set eventIds(newEventIds: number[]) {
    this._eventIds = uniq(newEventIds);
  }
  public get eventIds(): number[] {
    return this._eventIds;
  }

  @Output() public promocodeUpdated: EventEmitter<any> = new EventEmitter<any>();

  constructor(private snackService: SnackService,
              private storageService: StorageService,
              private authService: AuthService,
              public utilService: UtilService) {}

  public ngOnInit(): void {
    this.selfServicePromocode = null;
    if (!this._eventIds.length) {
      this.eventIds = this.service.cartEventIds;
    }
    this.getCodesFromStorageToCache();
  }

  public addPromoCode(): void {
    if (!this.promocode) return;
    if (this.selfService) {
      if (this.promocode === this.selfServicePromocode) {
        this.codesCacheAutocomplete.closePanel();
        this.clearPromocode();
        return;
      }
      this.service.addSelfServicePromocode({name: this.promocode}, this.eventIds[0]).pipe(
        tap({
          error: () => this.removeFromCache(this.promocode),
          finalize: () => this.service.retryCart(false, this.eventIds[0])
        })
      ).subscribe(() => this.finishPromocodeAdd());
    } else {
      this.service.addPromocode({name: this.promocode}).pipe(
        tap({error: () => {
          this.removeFromCache(this.promocode);
          this.service.retryCart();
        }})
      ).subscribe((newData) => {
        this.service.updateCart(newData);
        this.finishPromocodeAdd();
      });
    }
  }
  public removePromoCode(): void {
    if (this.selfService) {
      if (!this.eventPricing?.ticketTypes?.some((ticketType: EventPricingTicketType) => ticketType.promocode)) return;
      this.service.removeSelfServicePromocode(this.eventIds[0]).pipe(
        tap({finalize: () => this.service.retryCart(false, this.eventIds[0])})
      ).subscribe(() => this.clearPromocode());
    } else {
      if (!this.service.cart?.legacyPromocode) return;
      this.service.removePromocode({name: this.service.cart.legacyPromocode?.name}).pipe(
        tap({error: () => this.service.retryCart()})
      ).subscribe((newData) => {
        this.service.updateCart(newData);
        this.clearPromocode();
      });
    }
  }
  private finishPromocodeAdd(): void {
    this.addToCache(this.promocode);
    this.codesCacheAutocomplete.closePanel();
    this.clearPromocode();
  }
  private clearPromocode(): void {
    this.promocode = null;
    this.promocodeUpdated.emit();
  }

  public get cartLimits(): Limit[] {
    if (this.selfService || !this.service?.cart?.legacyPromocode) return [];
    const currentPromocode: Promocode = this.service.cart.legacyPromocode;
    const limits: Limit[] = currentPromocode?.limits || [];
    const correctEvent: boolean = currentPromocode.eventIds?.some((codeEventId: number) => this.eventIds.includes(codeEventId));
    return (correctEvent && limits?.filter(limit => limit.details && limit.details.length && limit.details.filter(detail => (detail.max - detail.used) < TICKET_LIMIT).length)) || [];
  }

  public detailsAvailableLimits(details: LimitDetail[]): LimitDetail[] {
    return orderBy(details.filter(detail => (detail.max - detail.used) < TICKET_LIMIT), 'priceType', 'asc');
  }
  public detailsPriceTypeCount(details: LimitDetail[], priceType: string): number {
    return this.detailsAvailableLimits(details).filter(detail => (detail.priceType === priceType)).length;
  }
  public detailsPriceTypeFirstIndex(details: LimitDetail[], priceType: string): number {
    return findIndex(this.detailsAvailableLimits(details), i => (i.priceType === priceType));
  }

  // CODE STORAGE
  private getCodesFromStorageToCache(): void {
    const codesCache = this.codesStorage[this.authService.user.userId];
    if (codesCache) {
      if (this.eventIds.length) {
        Object.keys(codesCache).forEach(eventId => {
          if (this.eventIds.includes(Number(eventId))) {
            this.codesCache.push(...codesCache[eventId]);
          }
        });
      } else {
        this.codesCache = codesCache['global'] || [];
      }
    }
  }
  private get codesStorage(): Record<string, Record<string, string[]>> {
    let codesStorage = this.storageService.get(STORAGE_PROMOCODE_STORAGE);
    if (codesStorage) {
      return JSON.parse(codesStorage);
    }
    return {};
  }

  private addToCache(codeToAdd: string): void {
    if (!this.codesCache.includes(codeToAdd)) {
      this.codesCache.push(codeToAdd);
    }
    this.addCodeToStorage(codeToAdd);
  }
  private removeFromCache(codeToRemove: string): void {
    this.codesCache = this.codesCache.filter(code => code !== codeToRemove);
    this.removeCodeFromStorage(codeToRemove);
  }

  private addCodeToStorage(codeToAdd: string): void {
    let codesStorage = this.codesStorage;
    const userId = this.authService.user.userId;
    if (!Object.keys(codesStorage).includes(userId)) {
      codesStorage[userId] = {};
    }
    if (this.eventIds.length) {
      this.eventIds.forEach(eventId => {
        if (!Object.keys(codesStorage[userId]).includes(eventId.toString())) {
          codesStorage[userId][eventId] = [codeToAdd];
        } else if (!codesStorage[userId][eventId].includes(codeToAdd)) {
          codesStorage[userId][eventId].push(codeToAdd);
        }
      });
    } else {
      if (!Object.keys(codesStorage[userId]).includes('global')) {
        codesStorage[userId]['global'] = [codeToAdd];
      } else if (!codesStorage[userId]['global'].includes(codeToAdd)) {
        codesStorage[userId]['global'].push(codeToAdd);
      }
    }
    this.storageService.set(STORAGE_PROMOCODE_STORAGE, JSON.stringify(codesStorage));
  }
  private removeCodeFromStorage(codeToRemove: string): void {
    let codesStorage = this.codesStorage;
    let userId = this.authService.user.userId;
    if (Object.keys(codesStorage).includes(userId)) {
      if (this.eventIds) {
        this.eventIds.forEach(eventId => {
          if (Object.keys(codesStorage[userId]).includes(eventId.toString())) {
            codesStorage[userId][eventId] = codesStorage[userId][eventId].filter((code) => code !== codeToRemove);
          }
        });
      } else {
        if (Object.keys(codesStorage[userId]).includes('global')) {
          codesStorage[userId]['global'] = codesStorage[userId]['global'].filter((code) => code !== codeToRemove);
        }
      }
      this.storageService.set(STORAGE_PROMOCODE_STORAGE, JSON.stringify(codesStorage));
    }
  }

  public codeSelectedFromCache(event: MatAutocompleteSelectedEvent): void {
    this.promocode = event.option.value;
    this.addPromoCode();
  }

  public get hasLegacyPromocode(): boolean {
    return !this.selfService && !!this.service.cart?.legacyPromocode;
  }
  public get hasSelfServicePromocode(): boolean {
    return this.selfService && !!this.selfServicePromocode;
  }
  public setActiveSelfServicePromocode(): void {
    this.eventPricing?.ticketTypes?.forEach(ticketType => {
      if (ticketType.promocode) {
        this.selfServicePromocode = ticketType.promocode;
      }
    });
  }
}
